'use client';
import React, { memo } from 'react';

import Modal from 'react-modal';
import 'react-image-gallery/styles/css/image-gallery.css';
import styled from 'styled-components';

import { ModalCloseButton } from '@components/Modal/next/Modal/utils';
import Gallery from '@components/galleries/Gallery';
import { getTypedFeatureProps } from '@views/hotel/detail/utils/features';
import { useDevice } from '@contexts/device';

import { FeatureLegendSection, RoomFeatures } from './FeatureSetRenderers';
import {
  Details,
  DetailsHeader,
  FeaturesBlock,
  ModalContent,
  ModalOverlay,
  GalleryContainer,
} from './styled';
import Mobile from './DetailsGalleriesModalMobile';
import { DetailsGalleriesModalProps, ModalView } from './types';

const NEXT_JS_ROOT = '#__next';

// Ensure Modal.setAppElement is only called in the browser
if (typeof window !== 'undefined' && document.querySelector(NEXT_JS_ROOT)) {
  Modal.setAppElement(NEXT_JS_ROOT);
} else {
  console.warn(
    'Modal.setAppElement could not find the root element. Make sure this is called in a browser environment.'
  );
}
export { ModalView };

const DetailsGalleriesModal = ({
  galleries,
  title,
  closeModal,
  isOpen,
  view,
  room,
  className,
  activeGalleryModalName,
  startImage,
  videos,
  control,
  ...rest
}: React.PropsWithChildren<DetailsGalleriesModalProps>) => {
  const featuresProps = getTypedFeatureProps(rest);
  const { isDesktop, isTablet } = useDevice();

  const hotelFeatures = rest.entityType === 'hotel';
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      shouldReturnFocusAfterClose={false}
      overlayElement={(props, contentElement) => (
        <ModalOverlay {...props}>{contentElement}</ModalOverlay>
      )}
      portalClassName={className}
      contentElement={
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ style, ...rest }, children) => (
          <ModalContent hotelFeatures={hotelFeatures} {...rest}>
            {children}
          </ModalContent>
        )
      }
    >
      {isDesktop ? (
        <GalleryContainer hotelFeatures={hotelFeatures}>
          <Gallery
            galleries={galleries}
            title={title ?? ''}
            closeGallery={closeModal}
            hideCategoryAll={rest.entityType === 'room'}
            activeGalleryModalName={activeGalleryModalName}
            startAtImage={startImage}
            closeModal={closeModal}
            entityType={rest.entityType}
            videos={videos}
            control={control}
          />
        </GalleryContainer>
      ) : (
        <Mobile
          closeModal={closeModal}
          galleries={galleries}
          view={view}
          room={room}
          videos={videos}
          control={control}
          {...featuresProps}
        />
      )}
      {isTablet ? (
        <GalleryContainer hotelFeatures={hotelFeatures}>
          <Gallery
            galleries={galleries}
            title={title ?? ''}
            closeGallery={closeModal}
            hideCategoryAll={rest.entityType === 'room'}
            activeGalleryModalName={activeGalleryModalName}
            startAtImage={startImage}
            closeModal={closeModal}
            entityType={rest.entityType}
            videos={videos}
            control={control}
          />
        </GalleryContainer>
      ) : null}
      <Details>
        <DetailsHeader>
          {isDesktop && rest.entityType === 'hotel' ? null : (
            <ModalCloseButton
              paletteColor={hotelFeatures ? 'NL0' : 'darkGray'}
              onClick={closeModal}
            />
          )}
        </DetailsHeader>
        {rest.entityType === 'room' ? (
          <>
            <FeatureLegendSection />
            <FeaturesBlock>
              <RoomFeatures features={rest.features} room={room} />
            </FeaturesBlock>
          </>
        ) : null}
      </Details>
    </Modal>
  );
};

const DetailsGalleriesModalWithPortalStyles = styled(DetailsGalleriesModal)`
  height: 100%;
`;
export default memo(DetailsGalleriesModalWithPortalStyles);
